import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Platform, PLATFORM_TO_HUMAN_READABLE } from '../../interfaces/db';
import { Typography } from '../../interfaces/typography';
import { useGlobalExtensionStatus, IntegrationAction } from './useIntegrationStatus';
import { TrellusDialerButton } from './TrellusDialerButton';
import { RootState } from '../../store';

// Helper functions for creating common instruction patterns
const createInstructions = {
  step: (text: string, url?: string) => (
    <div className="flex flex-col gap-1">
      <Typography variant="mediumParagraph" color="#1F2937" fontWeight={500}>
        {text}
      </Typography>
      {url && (
        <a 
          href={url.startsWith('http') ? url : `https://${url}`} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300 rounded-md transition-all duration-200 w-fit"
        >
          <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
          <Typography variant="smallParagraph" color="#6B7280" fontWeight={500}>
            {url}
          </Typography>
        </a>
      )}
    </div>
  ),
  note: (text: string) => <Typography variant="smallParagraph" color="#6B7280" className="italic">{text}</Typography>,
  numbered: (children: React.ReactNode, index: number) => (
    <div className="flex items-center gap-3">
      <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
        <Typography variant="smallParagraph" color="#2563EB" fontWeight={600}>
          {index + 1}
        </Typography>
      </div>
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  )
};

// Platform-specific instruction components
const PLATFORM_INSTRUCTIONS: Partial<Record<Platform, React.FC>> = {
  [Platform.HUBSPOT]: () => (
    <div className="flex flex-col gap-3">
      {createInstructions.numbered(
        createInstructions.step("Navigate to the Tasks tab via the sidebar"), 0
      )}
      {createInstructions.numbered(
        createInstructions.step("Filter whatever tasks you like and then click 'Parallel Dial Tasks'"), 1
      )}
    </div>
  ),
  
  [Platform.SALESLOFT]: () => (
    <div className="flex flex-col gap-3">
      {createInstructions.numbered(
        createInstructions.step("Open up your cadence/task list in the Salesloft sidebar"), 0
      )}
      {createInstructions.numbered(
        <div className="flex flex-col gap-1">
          {createInstructions.step("Select any task list or use Rhythm")}
          {createInstructions.note("Make sure tasks appear in the Salesloft UI before switching to the power parallel dialer")}
        </div>, 1
      )}
    </div>
  ),

  [Platform.OUTREACH]: () => (
    <div className="flex flex-col gap-3">
      {createInstructions.numbered(
        createInstructions.step("Either choose to 'Execute' tasks and you'll see the button"), 0
      )}
      {createInstructions.numbered(
        createInstructions.step("Or go to the main page or tasks page to see it show up"), 1
      )}
    </div>
  ),

  [Platform.APOLLO]: () => (
    <div className="flex flex-col gap-3">
      {createInstructions.numbered(
        createInstructions.step("Go to the tasks page", "https://app.apollo.io/#/tasks/"), 0
      )}
      {createInstructions.numbered(
        <div className="flex flex-col gap-1">
          {createInstructions.step("Note: For Apollo you need to enrich contacts beforehand")}
          {createInstructions.note("Contacts must be enriched before using the dialer")}
        </div>, 1
      )}
    </div>
  ),

  [Platform.SALESFORCE]: () => (
    <div className="flex flex-col gap-3">
      {createInstructions.numbered(
        createInstructions.step("Go to any contact report, contact list, opportunity, or lead list"), 0
      )}
      {createInstructions.numbered(
        createInstructions.step("You will see a parallel dialer option at the bottom toolbar"), 1
      )}
    </div>
  ),

  [Platform.CLAY]: () => (
    <div className="flex flex-col gap-3">
      {createInstructions.numbered(
        createInstructions.step("Go to a Clay table"), 0
      )}
      {createInstructions.numbered(
        createInstructions.step("You have to map the columns to the different options once you open the dialer"), 1
      )}
    </div>
  ),

  [Platform.GOOGLE_SHEETS]: () => (
    <div className="flex flex-col gap-3">
      {createInstructions.numbered(
        createInstructions.step("Go to any Google Sheet, you'll see a Trellus icon at the top right"), 0
      )}
      {createInstructions.numbered(
        createInstructions.step("Grant access to the sheet when requested, then map your columns to begin dialing"), 1
      )}
    </div>
  ),

  [Platform.GOHIGHLEVEL]: () => (
    <div className="flex flex-col gap-3">
      {createInstructions.numbered(
        createInstructions.step("Go to the Contacts tab"), 0
      )}
      {createInstructions.numbered(
        createInstructions.step("Filter for any contacts you care about with GoHighLevel's filters, then click the parallel dial button"), 1
      )}
    </div>
  ),

  [Platform.AMPLEMARKET]: () => (
    <div className="flex flex-col gap-3">
      {createInstructions.numbered(
        createInstructions.step("Go to the tasks page", "https://app.amplemarket.com/dashboard/tasks"), 0
      )}
      {createInstructions.numbered(
        createInstructions.step("You will see the button for 'Parallel Dial Tasks'"), 1
      )}
    </div>
  ),

  [Platform.ZOHO]: () => (
    <div className="flex flex-col gap-3">
      {createInstructions.numbered(
        createInstructions.step("Go to your tasks page (must use crm.zoho.com)", "https://crm.zoho.com"), 0
      )}
      {createInstructions.numbered(
        createInstructions.step("Note: The tasks have to have the word 'call' on them"), 1
      )}
    </div>
  ),

  [Platform.OPENPHONE]: () => (
    <div className="flex flex-col gap-3">
      {createInstructions.numbered(
        createInstructions.step("Upload your call list", "/upload"), 0
      )}
      {createInstructions.numbered(
        createInstructions.step("The dialer will be available after uploading your contacts"), 1
      )}
    </div>
  ),

  [Platform.VANILLASOFT]: () => (
    <div className="flex flex-col gap-3">
      {createInstructions.numbered(
        createInstructions.step("Go to the dialer view"), 0
      )}
      {createInstructions.numbered(
        createInstructions.step("A button will pop up after a few seconds at the bottom left for your dialer"), 1
      )}
    </div>
  ),

  [Platform.GONG_ENGAGE]: () => (
    <div className="flex flex-col gap-3">
      {createInstructions.numbered(
        createInstructions.step("Go to your Flow view to see all your tasks"), 0
      )}
      {createInstructions.numbered(
        createInstructions.step("You will see the Parallel Dial button available for your task list"), 1
      )}
    </div>
  )
};

// Platform configuration defining capabilities and display order
const PLATFORM_CONFIG = {
  // Popular/Main integrations (shown first) - ALL support both realtime and power dialer
  [Platform.HUBSPOT]: { isPopular: true, supportsRealtime: true, supportsPowerDialer: true, usesExpandedButton: true },
  [Platform.SALESLOFT]: { isPopular: true, supportsRealtime: true, supportsPowerDialer: true, usesExpandedButton: true },
  [Platform.OUTREACH]: { isPopular: true, supportsRealtime: true, supportsPowerDialer: true, usesExpandedButton: true },
  [Platform.APOLLO]: { isPopular: true, supportsRealtime: true, supportsPowerDialer: true, usesExpandedButton: true },
  [Platform.SALESFORCE]: { isPopular: true, supportsRealtime: true, supportsPowerDialer: true, usesExpandedButton: true },
  [Platform.CLAY]: { isPopular: true, supportsRealtime: true, supportsPowerDialer: true, usesExpandedButton: true },
  [Platform.GOOGLE_SHEETS]: { isPopular: true, supportsRealtime: true, supportsPowerDialer: true, usesExpandedButton: false },
  [Platform.GOHIGHLEVEL]: { isPopular: true, supportsRealtime: true, supportsPowerDialer: true, usesExpandedButton: true },
  [Platform.AMPLEMARKET]: { isPopular: true, supportsRealtime: true, supportsPowerDialer: true, usesExpandedButton: true },
  [Platform.ZOHO]: { isPopular: true, supportsRealtime: true, supportsPowerDialer: true, usesExpandedButton: true },
  [Platform.OPENPHONE]: { isPopular: true, supportsRealtime: true, supportsPowerDialer: true, usesExpandedButton: true },
  [Platform.VANILLASOFT]: { isPopular: true, supportsRealtime: true, supportsPowerDialer: true, usesExpandedButton: true },
  [Platform.GONG_ENGAGE]: { isPopular: true, supportsRealtime: true, supportsPowerDialer: true, usesExpandedButton: true },
  
  // Additional platforms (shown in collapsible section) - ONLY support realtime
  [Platform.AIRCALL]: { isPopular: false, supportsRealtime: true, supportsPowerDialer: false, usesExpandedButton: false },
  [Platform.CALLTOOLS]: { isPopular: false, supportsRealtime: true, supportsPowerDialer: false, usesExpandedButton: false },
  [Platform.DIALPAD]: { isPopular: false, supportsRealtime: true, supportsPowerDialer: false, usesExpandedButton: false },
  [Platform.FIVENINE]: { isPopular: false, supportsRealtime: true, supportsPowerDialer: false, usesExpandedButton: false },
  [Platform.NEXTIVA]: { isPopular: false, supportsRealtime: true, supportsPowerDialer: false, usesExpandedButton: false },
  [Platform.RINGCENTRAL]: { isPopular: false, supportsRealtime: true, supportsPowerDialer: false, usesExpandedButton: false },
};

// Derived arrays for backward compatibility
const POPULAR_PLATFORMS = Object.keys(PLATFORM_CONFIG).filter(
  platform => PLATFORM_CONFIG[platform as keyof typeof PLATFORM_CONFIG]?.isPopular
) as Platform[];

const ADDITIONAL_DIALER_PLATFORMS = Object.keys(PLATFORM_CONFIG).filter(
  platform => !PLATFORM_CONFIG[platform as keyof typeof PLATFORM_CONFIG]?.isPopular && 
              (PLATFORM_CONFIG[platform as keyof typeof PLATFORM_CONFIG]?.supportsRealtime || PLATFORM_CONFIG[platform as keyof typeof PLATFORM_CONFIG]?.supportsPowerDialer)
) as Platform[];

const ALL_PLATFORMS = [...POPULAR_PLATFORMS, ...ADDITIONAL_DIALER_PLATFORMS];

// Platforms that require parallel license
const PARALLEL_REQUIRED_PLATFORMS = [Platform.CLAY, Platform.SALESFORCE, Platform.GOOGLE_SHEETS, Platform.ZOHO, Platform.VANILLASOFT];

const PLATFORM_TO_LOGO: Partial<Record<Platform, string>> = {
  // Popular platforms
  [Platform.HUBSPOT]: "/assets/hubspot-logo.svg",
  [Platform.SALESLOFT]: "/assets/salesloft-logo.png", 
  [Platform.OUTREACH]: "/assets/outreach-logo.svg",
  [Platform.APOLLO]: "/assets/apollo.jpeg",
  [Platform.SALESFORCE]: "/assets/salesforce.svg",
  [Platform.CLAY]: "/assets/clay.png",
  [Platform.GOOGLE_SHEETS]: "/assets/sheets.png",
  [Platform.GOHIGHLEVEL]: "/assets/gohighlevel.svg",
  [Platform.AMPLEMARKET]: "/assets/amplemarket.svg",
  [Platform.ZOHO]: "/assets/zoho.svg",
  [Platform.OPENPHONE]: "/assets/openphone.png",
  [Platform.VANILLASOFT]: "/assets/vanillasoft.png",
  [Platform.GONG_ENGAGE]: "/assets/gong.png",
  // Additional dialer platforms
  [Platform.AIRCALL]: "/assets/aircall-logo.svg",
  [Platform.ALOWARE]: "/assets/aloware-logo.svg",
  [Platform.BITRIX]: "/assets/bitrix-logo.svg", 
  [Platform.BLOOBIRDS]: "/assets/bloobirds-logo.svg",
  [Platform.CALLTOOLS]: "/assets/calltools-logo.svg",
  [Platform.CONNECT_AND_SELL]: "/assets/connectandsell-logo.svg",
  [Platform.DIALPAD]: "/assets/dialpad-logo.svg",
  [Platform.FIVENINE]: "/assets/five9.png",
  [Platform.KOMMO]: "/assets/kommo-logo.svg",
  [Platform.NOOKS]: "/assets/nooks-logo.svg",
  [Platform.ORUM]: "/assets/orum-logo.svg",
  [Platform.NEXTIVA]: "/assets/nextiva-logo.svg",
  [Platform.RINGCENTRAL]: "/assets/ringcentral-logo.svg"
};

const PLATFORM_TO_URL: Partial<Record<Platform, string>> = {
  // Popular platforms
  [Platform.HUBSPOT]: "https://app.hubspot.com/",
  [Platform.SALESLOFT]: "https://app.salesloft.com/",
  [Platform.OUTREACH]: "https://web.outreach.io/",
  [Platform.APOLLO]: "https://app.apollo.io/",
  [Platform.SALESFORCE]: "https://login.salesforce.com/",
  [Platform.CLAY]: "https://clay.com/",
  [Platform.GOOGLE_SHEETS]: "https://sheets.google.com/",
  [Platform.GOHIGHLEVEL]: "https://app.gohighlevel.com/",
  [Platform.AMPLEMARKET]: "https://app.amplemarket.com/",
  [Platform.ZOHO]: "https://crm.zoho.com/",
  [Platform.OPENPHONE]: "https://my.openphone.com/",
  [Platform.VANILLASOFT]: "https://vanillasoft.net/",
  [Platform.GONG_ENGAGE]: "https://app.gong.io/",
  // Additional dialer platforms  
  [Platform.AIRCALL]: "https://aircall.com/",
  [Platform.DIALPAD]: "https://dialpad.com/",
  [Platform.FIVENINE]: "https://login.five9.com/",
  [Platform.NEXTIVA]: "https://nextiva.com/",
  [Platform.RINGCENTRAL]: "https://app.ringcentral.com/"
};

// Helper function to generate integration URLs
export const getIntegrationUrl = (platform: Platform): string => {
  return `/settings?tab=Integrations&platform=${platform}`;
};

// Helper function to get all supported integration platforms
export const getSupportedIntegrationPlatforms = (): Platform[] => {
  return ALL_PLATFORMS;
};

interface Integration {
  platform: Platform;
  name: string;
  logo?: string;
  isActive: boolean;
  requiresActivation: boolean;
  statusMessage: string;
  actionRequired: IntegrationAction;
}


export function Integrations() {
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showAdditional, setShowAdditional] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get global extension status
  const extensionStatus = useGlobalExtensionStatus();
  
  // Check parallel access
  const hasParallelAccess = useSelector((state: RootState) => state.multiDialerQuota.value !== null);
  
  // Check if integrations are still loading
  const areIntegrationsLoading = extensionStatus.checking;
  
  const allIntegrations = React.useMemo((): Integration[] => {
    return ALL_PLATFORMS
      .filter(platform => PLATFORM_CONFIG[platform as keyof typeof PLATFORM_CONFIG]) // Only include configured platforms
      .map(platform => {
        const { installed, activated, activatedEverywhere, checking } = extensionStatus;
        
        let statusMessage = 'Available';
        let actionRequired = IntegrationAction.NONE;
        let isActive = false;

        if (checking) {
          statusMessage = 'Checking...';
          actionRequired = IntegrationAction.INSTALL_EXTENSION;
        } else if (!installed) {
          statusMessage = 'Extension required';
          actionRequired = IntegrationAction.INSTALL_EXTENSION;
        } else if (!activated) {
          statusMessage = 'Extension inactive';
          actionRequired = IntegrationAction.ACTIVATE_EXTENSION;
        } else if (platform === Platform.GOHIGHLEVEL && !activatedEverywhere) {
          statusMessage = 'Activation required';
          actionRequired = IntegrationAction.COMPLETE_SECONDARY_SETUP;
        } else if (PARALLEL_REQUIRED_PLATFORMS.includes(platform) && !hasParallelAccess) {
          statusMessage = 'Parallel license required';
          actionRequired = IntegrationAction.UPGRADE_TO_PARALLEL;
        } else {
          statusMessage = 'Connected';
          actionRequired = IntegrationAction.NONE;
          isActive = true;
        }

        return {
          platform,
          name: PLATFORM_TO_HUMAN_READABLE[platform],
          logo: PLATFORM_TO_LOGO[platform],
          isActive,
          requiresActivation: actionRequired !== IntegrationAction.NONE,
          statusMessage,
          actionRequired
        };
      });
  }, [extensionStatus, hasParallelAccess]);

  const popularIntegrations = allIntegrations.filter(i => POPULAR_PLATFORMS.includes(i.platform));
  const additionalIntegrations = allIntegrations.filter(i => ADDITIONAL_DIALER_PLATFORMS.includes(i.platform));

  // Filter integrations based on search query
  const filteredPopular = searchQuery.trim() 
    ? popularIntegrations.filter(integration => 
        integration.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : popularIntegrations;
    
  const filteredAdditional = searchQuery.trim()
    ? additionalIntegrations.filter(integration => 
        integration.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : additionalIntegrations;
  
  // Auto-expand additional section if search matches additional integrations
  const hasAdditionalResults = filteredAdditional.length > 0 && searchQuery.trim();
  const shouldShowAdditional = showAdditional || hasAdditionalResults;

  // Helper functions for integration actions
  const getActionDescription = (action: IntegrationAction): string => {
    const descriptions = {
      [IntegrationAction.INSTALL_EXTENSION]: 'Install the Trellus browser extension to get started with this integration.',
      [IntegrationAction.ACTIVATE_EXTENSION]: 'Activate the Trellus extension in your browser extensions menu.',
      [IntegrationAction.GRANT_PERMISSIONS]: 'Grant the extension permission to access this platform\'s website.',
      [IntegrationAction.COMPLETE_SECONDARY_SETUP]: 'Complete additional configuration steps for this platform.',
      [IntegrationAction.UPGRADE_TO_PARALLEL]: 'This platform requires a Parallel Dialer license. Upgrade your plan to unlock power dialing capabilities with this integration.',
      [IntegrationAction.NONE]: 'This integration is fully configured and ready to use.'
    };
    return descriptions[action] || '';
  };

  const getActionButtonText = (action: IntegrationAction): string => {
    const buttonTexts: Partial<Record<IntegrationAction, string>> = {
      [IntegrationAction.INSTALL_EXTENSION]: 'Install Extension',
      [IntegrationAction.ACTIVATE_EXTENSION]: 'Activate Extension',
      [IntegrationAction.GRANT_PERMISSIONS]: 'Grant Permissions',
      [IntegrationAction.COMPLETE_SECONDARY_SETUP]: 'Complete Setup',
      [IntegrationAction.UPGRADE_TO_PARALLEL]: 'View Pricing & Upgrade'
    };
    return buttonTexts[action] || 'Fix Issue';
  };

  const handleActionRequired = (action: IntegrationAction) => {
    const actionUrls: Partial<Record<IntegrationAction, string>> = {
      [IntegrationAction.INSTALL_EXTENSION]: 'https://chrome.google.com/webstore/detail/trellus/enhpjjojmnlnaokmppkkifgaonfojigl',
      [IntegrationAction.ACTIVATE_EXTENSION]: 'chrome://extensions/',
      [IntegrationAction.GRANT_PERMISSIONS]: '/upgrade',
      [IntegrationAction.COMPLETE_SECONDARY_SETUP]: '/upgrade_ghl',
      [IntegrationAction.UPGRADE_TO_PARALLEL]: 'https://buy.stripe.com/5kA00h0lY5qb3V67vT'
    };
    
    const url = actionUrls[action];
    if (url) {
      window.open(url, '_blank');
    }
  };

  const getCurrentPlatform = useCallback(() => {
    return new URLSearchParams(location.search).get("platform");
  }, [location]);

  const updateSelectedIntegration = (integration: Integration | null) => {
    const searchParams = new URLSearchParams(location.search);
    
    if (integration) {
      searchParams.set('platform', integration.platform);
    } else {
      searchParams.delete('platform');
    }
    
    navigate(`/settings?${searchParams.toString()}`, { replace: true });
  };

  useEffect(() => {
    const platformParam = getCurrentPlatform();
    if (platformParam) {
      const integration = allIntegrations.find(i => i.platform === platformParam);
      if (integration) {
        setSelectedIntegration(integration);
      }
    } else {
      setSelectedIntegration(null);
    }
  }, [location, getCurrentPlatform, allIntegrations]);

  const getStatusColor = (integration: Integration) => {
    if (integration.isActive) return { bg: 'bg-emerald-500', text: '#059669' };
    if (integration.actionRequired === IntegrationAction.INSTALL_EXTENSION) return { bg: 'bg-red-500', text: '#DC2626' };
    if (integration.actionRequired === IntegrationAction.COMPLETE_SECONDARY_SETUP) return { bg: 'bg-red-500', text: '#DC2626' };
    if (integration.actionRequired === IntegrationAction.UPGRADE_TO_PARALLEL) return { bg: 'bg-blue-500', text: '#2563EB' };
    return { bg: 'bg-yellow-500', text: '#D97706' };
  };

  const renderIntegrationCard = (integration: Integration) => {
    const statusColor = getStatusColor(integration);
    const hasActionRequired = integration.actionRequired !== IntegrationAction.NONE;
    
    return (
      <div
        key={integration.platform}
        onClick={() => updateSelectedIntegration(integration)}
        className={`group relative p-4 rounded-lg cursor-pointer transition-all duration-200 bg-white hover:bg-gray-50 border hover:border-emerald-300 hover:shadow-sm flex items-center gap-3 min-h-[70px] ${
          hasActionRequired 
            ? `border-red-200 shadow-sm` 
            : 'border-gray-200'
        }`}
      >
        <div className="w-10 h-10 rounded-md flex items-center justify-center bg-gray-50 flex-shrink-0">
          {integration.logo ? (
            <img
              src={integration.logo}
              alt={`${integration.name} logo`}
              className="w-6 h-6 object-contain"
            />
          ) : (
            <div className="w-6 h-6 bg-gray-300 rounded flex items-center justify-center">
              <span className="text-xs font-medium text-gray-600">
                {integration.name.charAt(0)}
              </span>
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex flex-col flex-1 min-w-0">
              <Typography 
                variant="mediumParagraph" 
                color="#1F2937" 
                fontWeight={500}
                className="truncate"
              >
                {integration.name}
              </Typography>
              <Typography 
                variant="smallParagraph" 
                color="#6B7280"
                className="group-hover:text-emerald-600 transition-colors"
              >
                Click to learn how to use →
              </Typography>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Typography 
                variant="smallParagraph" 
                color={statusColor.text}
                className="text-right"
                fontWeight={500}
              >
                {integration.statusMessage}
              </Typography>
              <div className={`w-2 h-2 rounded-full ${statusColor.bg}`} />
            </div>
          </div>
        </div>

        {/* Click indicator arrow */}
        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    );
  };

  const renderAdditionalIntegrationCard = (integration: Integration) => {
    const statusColor = getStatusColor(integration);
    const hasActionRequired = integration.actionRequired !== IntegrationAction.NONE;
    
    return (
      <div
        key={integration.platform}
        onClick={() => updateSelectedIntegration(integration)}
        className={`group relative p-3 rounded-lg cursor-pointer transition-all duration-200 bg-white hover:bg-gray-50 border hover:border-emerald-300 hover:shadow-sm flex items-center justify-between min-h-[50px] ${
          hasActionRequired 
            ? `border-red-200 shadow-sm` 
            : 'border-gray-200'
        }`}
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex flex-col flex-1 min-w-0">
            <Typography 
              variant="mediumParagraph" 
              color="#1F2937" 
              fontWeight={500}
              className="truncate"
            >
              {integration.name}
            </Typography>
            <Typography 
              variant="smallParagraph" 
              color="#6B7280"
              className="group-hover:text-emerald-600 transition-colors"
            >
              Click to learn →
            </Typography>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Typography 
              variant="smallParagraph" 
              color={statusColor.text}
              fontWeight={500}
            >
              {integration.statusMessage}
            </Typography>
            <div className={`w-2 h-2 rounded-full ${statusColor.bg}`} />
            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderIntegrationDetails = (integration: Integration) => {
    const config = PLATFORM_CONFIG[integration.platform as keyof typeof PLATFORM_CONFIG];
    const supportsPowerDialer = config?.supportsPowerDialer;
    const supportsRealtime = config?.supportsRealtime;
    
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            {/* Only show images for popular platforms */}
            {POPULAR_PLATFORMS.includes(integration.platform) && integration.logo && (
              <div className="w-16 h-16 rounded-lg bg-gray-50 flex items-center justify-center">
                <img
                  src={integration.logo}
                  alt={`${integration.name} logo`}
                  className="w-10 h-10 object-contain"
                />
              </div>
            )}
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-3">
                <Typography variant="h3" color="#1F2937" fontWeight={600}>
                  {integration.name}
                </Typography>
                {PLATFORM_TO_URL[integration.platform] && (
                  <a
                    href={PLATFORM_TO_URL[integration.platform]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300 rounded-lg transition-all duration-200"
                  >
                    <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    <Typography variant="smallParagraph" color="#6B7280" fontWeight={500}>
                      Open {integration.name}
                    </Typography>
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className={`
                  w-2 h-2 rounded-full
                  ${integration.isActive ? 'bg-green-500' : 'bg-gray-400'}
                `} />
                <Typography 
                  variant="mediumParagraph" 
                  color={integration.isActive ? "#059669" : "#6B7280"}
                >
                  {integration.isActive ? "Active" : "Inactive"}
                </Typography>
              </div>
            </div>
          </div>
          <button
            onClick={() => updateSelectedIntegration(null)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex flex-col gap-8">
          {/* Status Section */}
          <div className="flex flex-col gap-4">
            <Typography variant="h4" color="#1F2937" fontWeight={600}>
              Status
            </Typography>
            <div className={`
              p-4 rounded-lg border-l-4 
              ${integration.isActive 
                ? 'bg-emerald-50 border-emerald-500' 
                : (integration.actionRequired === IntegrationAction.INSTALL_EXTENSION || integration.actionRequired === IntegrationAction.COMPLETE_SECONDARY_SETUP)
                  ? 'bg-red-50 border-red-500'
                  : integration.actionRequired === IntegrationAction.UPGRADE_TO_PARALLEL
                    ? 'bg-blue-50 border-blue-500'
                    : 'bg-yellow-50 border-yellow-500'
              }
            `}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-2 flex-1">
                  <Typography 
                    variant="mediumParagraph" 
                    color={integration.isActive 
                      ? "#059669" 
                      : (integration.actionRequired === IntegrationAction.INSTALL_EXTENSION || integration.actionRequired === IntegrationAction.COMPLETE_SECONDARY_SETUP) 
                        ? "#DC2626" 
                        : integration.actionRequired === IntegrationAction.UPGRADE_TO_PARALLEL
                          ? "#2563EB"
                          : "#D97706"
                    }
                    fontWeight={600}
                  >
                    {integration.statusMessage}
                  </Typography>
                  <Typography 
                    variant="smallParagraph" 
                    color="#6B7280"
                  >
                    {getActionDescription(integration.actionRequired)}
                  </Typography>
                </div>
                {integration.actionRequired !== IntegrationAction.NONE && (
                  <button
                    onClick={() => handleActionRequired(integration.actionRequired)}
                    className={`
                      px-4 py-2 rounded-lg text-sm transition-all duration-200 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-2
                      ${(integration.actionRequired === IntegrationAction.INSTALL_EXTENSION || integration.actionRequired === IntegrationAction.COMPLETE_SECONDARY_SETUP)
                        ? 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-300'
                        : integration.actionRequired === IntegrationAction.UPGRADE_TO_PARALLEL
                          ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 focus:ring-blue-300'
                          : 'bg-yellow-600 hover:bg-yellow-700 text-white focus:ring-yellow-300'
                      }
                    `}
                  >
                    <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={500}>
                      {getActionButtonText(integration.actionRequired)}
                    </Typography>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Loading Your Dialer with Tasks - For platforms with Power Dialer support */}
          {supportsPowerDialer && (
            <div className="flex flex-col gap-6">
              <Typography variant="h4" color="#1F2937" fontWeight={600}>
                Loading Your Dialer with Tasks
              </Typography>
              
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-100">
                <div className="flex flex-col gap-6">
                  {/* Platform-specific navigation instructions */}
                  {PLATFORM_INSTRUCTIONS[integration.platform] && (
                    <div className="flex flex-col gap-4">
                      <Typography variant="mediumParagraph" color="#1F2937" fontWeight={600}>
                        Navigation Steps
                      </Typography>
                      {React.createElement(PLATFORM_INSTRUCTIONS[integration.platform]!)}
                    </div>
                  )}

                  {/* Dialer Button Section */}
                  <div className="flex flex-col gap-4">
                    <div className="h-px bg-blue-200"></div>
                    <Typography variant="mediumParagraph" color="#1F2937" fontWeight={600}>
                      Look for the Trellus Dialer Button
                    </Typography>
                    <div className="flex items-center gap-4 p-4 bg-white rounded-lg border border-blue-200 shadow-sm">
                      <TrellusDialerButton 
                        variant={config?.usesExpandedButton ? "expanded" : "minimal"} 
                        mode="dialer" 
                        dialerType="Parallel" 
                      />
                      <Typography variant="smallParagraph" color="#6B7280">
                        This button will appear in your {integration.name} interface
                      </Typography>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-blue-100 rounded-lg">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <Typography variant="smallParagraph" color="#1E40AF" fontWeight={600}>
                        Use the interactive demo below to learn how to use it effectively
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Realtime Instructions - For platforms that only support realtime */}
          {!supportsPowerDialer && supportsRealtime && (
            <div className="flex flex-col gap-4">
              <Typography variant="h4" color="#1F2937" fontWeight={600}>
                Using Trellus with {integration.name}
              </Typography>
              <div className="bg-green-50 p-6 rounded-lg">
                <div className="flex flex-col gap-3">
                  <Typography variant="mediumParagraph" color="#1F2937" fontWeight={500}>
                    Real-time coaching is automatically available
                  </Typography>
                  <Typography variant="mediumParagraph" color="#6B7280">
                    Simply place a call using {integration.name}'s native dialer and Trellus' real-time coaching system will automatically appear to assist you during your conversation.
                  </Typography>
                </div>
              </div>
            </div>
          )}

          {/* Interactive Demo - Only for platforms with Power Dialer support */}
          {supportsPowerDialer && (
            <div className="flex flex-col gap-4">
              <Typography variant="h4" color="#1F2937" fontWeight={600}>
                Interactive Demo
              </Typography>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <iframe
                  className="responsive-embed w-full"
                  src="https://story.screenspace.io/trellus/e_5b948929?&zoom=7"
                  width="100%"
                  height="600"
                  scrolling="no"
                  allow="autoplay; fullscreen; clipboard-write"
                  title={`${integration.name} Dialer Demo`}
                />
              </div>
              <Typography variant="smallParagraph" color="#9CA3AF">
                Click through the demo above to see how the Trellus dialer works with {integration.name}
              </Typography>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 bg-gray-50 min-h-full">
      {selectedIntegration ? (
        renderIntegrationDetails(selectedIntegration)
      ) : (
        <div className="max-w-6xl">
          <div className="flex flex-col gap-6">
            {/* Extension Status Banner - Show appropriate state */}
            {extensionStatus.checking && (
              <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <div className="flex flex-col gap-1">
                  <Typography variant="mediumParagraph" color="#2563EB" fontWeight={600}>
                    Checking Extension Status
                  </Typography>
                  <Typography variant="smallParagraph" color="#1E40AF">
                    Please wait while we verify your browser extension
                  </Typography>
                </div>
              </div>
            )}

            {!extensionStatus.checking && !extensionStatus.installed && (
              <div className="flex items-center justify-between gap-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Typography variant="mediumParagraph" color="#DC2626" fontWeight={600}>
                      Extension Required
                    </Typography>
                    <Typography variant="smallParagraph" color="#7F1D1D">
                      Install the Trellus browser extension to start using integrations
                    </Typography>
                  </div>
                </div>
                <a
                  href="https://chrome.google.com/webstore/detail/trellus/enhpjjojmnlnaokmppkkifgaonfojigl"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors flex-shrink-0"
                >
                  Install Extension
                </a>
              </div>
            )}

            {!extensionStatus.checking && extensionStatus.installed && !extensionStatus.activated && (
              <div className="flex items-center justify-between gap-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Typography variant="mediumParagraph" color="#D97706" fontWeight={600}>
                      Extension Activation Required
                    </Typography>
                    <Typography variant="smallParagraph" color="#92400E">
                      Activate your Trellus extension to unlock all integrations
                    </Typography>
                  </div>
                </div>
                <button
                  onClick={() => window.open('/upgrade', '_blank')}
                  className="px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700 transition-colors flex-shrink-0"
                >
                  Activate Extension
                </button>
              </div>
            )}

            {!extensionStatus.checking && extensionStatus.installed && extensionStatus.activated && (
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="flex flex-col gap-1">
                  <Typography variant="mediumParagraph" color="#059669" fontWeight={600}>
                    Extension Fully Activated
                  </Typography>
                  <Typography variant="smallParagraph" color="#047857">
                    All integrations are ready to use
                  </Typography>
                </div>
              </div>
            )}

            {/* Header */}
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <Typography variant="h3" color="#1F2937" fontWeight={600}>
                  Integrations
                </Typography>
                <Typography variant="mediumParagraph" color="#6B7280">
                  Connect Trellus with your platforms
                </Typography>
              </div>

              {/* Search Bar */}
              <div className="relative w-full max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg 
                    className="h-5 w-5 text-gray-400" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-3 text-sm bg-white border border-gray-200 rounded-lg placeholder-gray-500 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:shadow-sm"
                  placeholder="Search integrations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Dialer + Real-time Integrations */}
            <div className="flex flex-col gap-4">
              <Typography variant="h4" color="#1F2937" fontWeight={600}>
                Dialer + Real-time
              </Typography>
              {areIntegrationsLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="p-4 bg-white border border-gray-200 rounded-lg animate-pulse">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-md"></div>
                        <div className="flex-1 flex flex-col gap-2">
                          <div className="h-4 bg-gray-200 rounded"></div>
                          <div className="h-3 bg-gray-100 rounded w-2/3"></div>
                        </div>
                        <div className="w-2 h-2 bg-gray-200 rounded-full"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredPopular.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {filteredPopular.map(renderIntegrationCard)}
                </div>
              ) : searchQuery.trim() ? (
                <div className="flex justify-center py-6">
                  <Typography variant="mediumParagraph" color="#6B7280">
                    No dialer + real-time integrations match your search
                  </Typography>
                </div>
              ) : null}
            </div>

            {/* Real-time Only Integrations */}
            <div className="flex flex-col gap-4">
              <div 
                className="flex items-center justify-between cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors"
                onClick={() => setShowAdditional(!showAdditional)}
              >
                <div className="flex items-center gap-2">
                  <Typography variant="h4" color="#1F2937" fontWeight={600}>
                    Real-time Only
                  </Typography>
                  <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                    {additionalIntegrations.length}
                  </span>
                  {hasAdditionalResults && (
                    <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full">
                      {filteredAdditional.length} matches
                    </span>
                  )}
                </div>
                <svg 
                  className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${shouldShowAdditional ? 'rotate-180' : ''}`}
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              
              {shouldShowAdditional && (
                <div className="flex flex-col gap-4 pl-2">
                  {areIntegrationsLoading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {Array.from({ length: 3 }).map((_, index) => (
                        <div key={index} className="p-3 bg-white border border-gray-200 rounded-lg animate-pulse">
                          <div className="flex items-center justify-between">
                            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                            <div className="w-2 h-2 bg-gray-200 rounded-full"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : filteredAdditional.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {filteredAdditional.map(renderAdditionalIntegrationCard)}
                    </div>
                  ) : searchQuery.trim() ? (
                    <div className="text-center py-6">
                      <Typography variant="mediumParagraph" color="#6B7280">
                        No real-time only integrations match your search
                      </Typography>
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {/* No results message */}
            {searchQuery.trim() && filteredPopular.length === 0 && filteredAdditional.length === 0 && (
              <div className="flex flex-col items-center gap-3 py-8">
                <div className="text-gray-400">
                  <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <Typography variant="mediumParagraph" color="#6B7280" fontWeight={500}>
                    No integrations found
                  </Typography>
                  <Typography variant="smallParagraph" color="#9CA3AF">
                    Try adjusting your search terms
                  </Typography>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}